import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback, useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery, useMutation, gql } from '@zenbusiness/application-commons-graphql';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import Breadcrumbs from '#client/components/Breadcrumbs';
import empty from '#client/utils/empty';
import exists from '#client/utils/exists';
import { sortAsc } from '#client/utils/sort';
import { getErrorsByField, throwExceptions } from '#client/utils/validation';

const sortAscByCreatedDatetime = sortAsc('createdDatetime');

const DOMAIN_NAMESERVER_MUTATE_READ = gql`
  query DomainNameserverRead($domainUuid: UUID!) {
    domain(domainUuid: $domainUuid) {
      domainUuid
      useZenbusinessNameservers
      domainNameservers {
        domainNameserverUuid
        name
        deleted
        createdDatetime
        updatedDatetime
      }
    }
  }
`;

const DOMAIN_NAMESERVER_MUTATE_UPDATE = gql`
  mutation DomainNameserverUpdate($input: updateDomainAndNameserversInput!) {
    updateDomainAndNameservers(input: $input) {
      domainUuid
      useZenbusinessNameservers
      domainNameservers {
        domainNameserverUuid
        name
        deleted
        createdDatetime
        updatedDatetime
      }
    }
  }
`;

const DOMAIN_READ = gql`
  query Domain($domainUuid: UUID!) {
    domain(domainUuid: $domainUuid) {
      domainUuid
      useZenbusinessNameservers
      domainNameservers {
        domainNameserverUuid
        name
        deleted
        createdDatetime
        updatedDatetime
      }
    }
  }
`;

const messages = {
  config: {
    name: 'Name'
  },
  validation: {
    generic: 'Error',
    name: {
      format: 'Name must be a valid hostname',
      maxLength: 'Name may not be more than 254 characters',
      invalid_hostname: 'Name must be a valid hostname',
      nameserver_does_not_exist_at_registry: 'Please contact ZenBusiness customer support to save this nameserver'
    },
    useZenbusinessNameservers: {
      min_2_custom_nameservers_required: 'Two valid custom nameservers required to switch to using custom nameservers'
    }
  },
  help: {
    common: `For instructions on setting up your nameservers check with your website or hosting
      provider, such as WIX, Squarespace, or Weebly. A minimum of two nameservers are required.
      If you choose to have active nameserver, DNS zone information will not be active, which
      means that any existing forwarding and DNS options will be disabled.`,
    nameserver: `Specify your own nameservers, a minimum of two are required. If you choose to have active
      nameservers, DNS zone information will not be active, which means that any existing forwarding
      and DNS options will be disabled.`
  }
};

const StyledDivRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2)
}));

const StyledGridMasthead = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  width: '100%',
  '& .MuiInputBase-formControl': {
    borderRadius: 16
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: theme.spacing(7),
  textTransform: 'capitalize',
  '&.inline': {
    width: 'auto'
  },
  '&:disabled': {
    background: theme.palette.focus30.main
  }
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  background: theme.palette.focus30.main,
  height: 1,
  margin: 0,
  marginBottom: theme.spacing(3)
}));

const StyledLegend = styled(FormLabel)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.focus70.main
}));

const StyledForm = styled('form')(() => ({
  width: '100%'
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2)
}));

const StyledHelpTitle = styled(Title)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));

const StyledActions = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row-reverse'
  }
}));

const StyledTrash = styled(SharedAsset)(({ theme }) => ({
  width: 20,
  height: 20,
  fill: theme.palette.error.light,
  stroke: theme.palette.error.light
}));

const StyledTitlePage = styled(Title)(({ theme }) => ({
  ...theme.typography.title_extraLarge
}));

const DomainNameserverMutate = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const {
    loading: readLoading,
    data: readData
  } = useQuery(DOMAIN_NAMESERVER_MUTATE_READ, {
    variables: {
      domainUuid
    }
  });
  const [updateDomainAndNameservers, {
    loading: updateLoading,
    error: updateError,
    data: updateData
  }] = useMutation(DOMAIN_NAMESERVER_MUTATE_UPDATE, {
    noThrow: true,
    update(cache, { data }) {
      try {
        const { updateDomainAndNameservers: updatedDomainAndNameservers } = data;
        const { domain } = cache.readQuery({
          query: DOMAIN_READ,
          variables: {
            domainUuid
          }
        })?.data || {};

        cache.writeQuery({
          query: DOMAIN_READ,
          data: {
            domain: {
              ...domain,
              domainNameservers: updatedDomainAndNameservers.domainNameservers
            }
          }
        });
      } catch {
        // Failed to update cache. The queried data doesn't exit in the cache,
        // because the user hit the page directly or refreshed the page. Ignore error
      }
    }
  });

  // const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [isSetUseZenbusinessNameservers, setUseZenbusinessNameservers] = useState(null);
  const [changes, setChanges] = useState({});

  const loading = readLoading || updateLoading;

  throwExceptions(updateError);

  const updatedDomainAndNameservers = updateData && updateData.updateDomainAndNameservers;
  const domain = readLoading ? {} : (
    (updatedDomainAndNameservers) || (readData && readData.domain)
  );

  const unsavedUse = isSetUseZenbusinessNameservers !== null
    && isSetUseZenbusinessNameservers !== domain.useZenbusinessNameservers;
  const unsavedChanges = !empty(changes);
  const unsaved = unsavedChanges || unsavedUse;

  const domainNameservers = (domain && domain.domainNameservers) || [];

  const handleAddNameserver = () => {
    setChanges({
      ...changes,
      [uuid()]: {
        name: '',
        create: true
      }
    });
  };

  const handleRecordChange = (event) => {
    const {
      name: targetUuid,
      value: name
    } = event.target;

    const targetedExists = !!domainNameservers.find(
      ({ domainNameserverUuid }) => domainNameserverUuid === targetUuid
    );

    setChanges({
      ...changes,
      [targetUuid]: {
        name,
        create: !targetedExists
      }
    });
  };

  const handleDeleteNameserver = (uuidToDelete) => () => {
    const targetedExists = !!domainNameservers.find(
      ({ domainNameserverUuid }) => domainNameserverUuid === uuidToDelete
    );

    const modifiedChanges = {
      ...changes
    };

    if (!targetedExists) {
      delete modifiedChanges[uuidToDelete];
    } else {
      modifiedChanges[uuidToDelete] = {
        ...modifiedChanges[uuidToDelete],
        name: null
      };
    }

    setChanges({
      ...modifiedChanges
    });
  };

  const useZenbusinessNameserversValue = exists(isSetUseZenbusinessNameservers)
    ? isSetUseZenbusinessNameservers : domain.useZenbusinessNameservers || false;

  const handleUseZenbusinessNameservers = () => {
    setUseZenbusinessNameservers(!useZenbusinessNameserversValue);
  };

  const handleButtonPressedCallback = useButtonPressedCallback({
    elementName: 'save_nameserver',
    pageCategory: 'dashboard',
    pageSection: 'home',
    screenName: 'nameservers',
    buttonName: 'save_nameserver'
  });

  const handleOverviewClick = useLinkPressedCallback({
    elementName: 'overview_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'home',
    screenName: 'nameservers',
    buttonName: 'overview_breadcrumb'
  });

  const handleDomainClick = useLinkPressedCallback({
    elementName: 'domain_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'home',
    screenName: 'nameservers',
    buttonName: 'domain_breadcrumb'
  });

  const handleDomainsClick = useLinkPressedCallback({
    elementName: 'domains_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'home',
    screenName: 'nameservers',
    buttonName: 'domains_breadcrumb'
  });

  const handleNameserverSave = () => {
    if (loading || !unsaved) {
      return;
    }

    handleButtonPressedCallback();

    let mutatedDomainNameservers;
    if (unsavedChanges) {
      mutatedDomainNameservers = Object.entries(changes).map(
        ([domainNameserverUuid, { name, create }]) => ({
          domainNameserverUuid,
          name,
          create
        })
      );
    }

    const useZenbusinessNameservers = exists(isSetUseZenbusinessNameservers)
      ? isSetUseZenbusinessNameservers : domain.useZenbusinessNameservers || false;

    updateDomainAndNameservers({
      variables: {
        input: {
          domainUuid,
          useZenbusinessNameservers,
          domainNameservers: mutatedDomainNameservers
        }
      }
    });
  };

  if (!loading && (updatedDomainAndNameservers)) {
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}#nameservers`} />);
  }

  // Process server validation errors
  const errorsByField = getErrorsByField(updateError);

  // Handle dynamic record configuration fields
  const records = [];
  // Load saved data
  domainNameservers
    .filter(({ deleted }) => (!deleted))
    .sort(sortAscByCreatedDatetime)
    .forEach(({
      domainNameserverUuid: key,
      name: value,
      create
    }) => {
      const existingValue = value || '';
      const changedValue = (exists(changes[key]) ? changes[key] : null);
      const errorKey = exists(errorsByField[key]) && errorsByField[key][0];
      let error = null;
      if (errorKey) {
        error = messages.validation.name[errorKey] || messages.validation.generic;
      }

      records.push({
        label: 'name',
        key,
        value: exists(changedValue) ? changedValue : existingValue,
        required: true,
        create,
        error
      });
    });
  // Load unsaved data
  Object.entries(changes)
    .forEach(([key, nsProps = {}]) => {
      const { name: value, create = false } = nsProps;
      if (create) {
        const errorKey = exists(errorsByField[key]) && errorsByField[key][0];
        let error = null;
        if (errorKey) {
          error = messages.validation.name[errorKey] || messages.validation.generic;
        }
        records.push({
          label: 'name',
          key,
          value,
          required: false,
          create,
          error
        });
        return;
      }
      const existingIndex = records.findIndex(({ key: existingKey }) => (existingKey === key));
      if (value === null) {
        // Remove unsaved deletion changes from UI
        records.splice(existingIndex, 1);
      } else {
        // Else update UI with name change
        records[existingIndex] = {
          ...records[existingIndex],
          value
        };
      }
    });

  if (records.length < 2) {
    while (records.length < 2) {
      records.push({
        label: 'name',
        key: uuid(),
        value: '',
        create: true,
        required: true,
        error: null
      });
    }
  }

  const showDelete = !loading && records.length > 2;
  const useZenbusinessNameserverError = errorsByField.useZenbusinessNameservers;

  return (
    <StyledDivRoot>
      <Breadcrumbs gutterBottom>
        <Button
          variant="text"
          color="primary"
          component={RouterLink}
          to={`/d/company/${businessEntityUuid}/marketing-tools`}
          onClick={handleOverviewClick}
        >
          Overview
        </Button>
        <Button
          variant="text"
          color="primary"
          component={RouterLink}
          to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}
          onClick={handleDomainsClick}
        >
          Domains
        </Button>
        <Button
          variant="text"
          color="primary"
          component={RouterLink}
          to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}
          onClick={handleDomainClick}
        >
          Domain
        </Button>
        <Button
          variant="text"
          disabled
          color="primary"
          component={RouterLink}
          to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/nameservers`}
        >
          Nameserver
        </Button>
      </Breadcrumbs>

      <StyledGridMasthead
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <StyledTitlePage
            variant="lg-alt"
            level={2}
          >
            Edit Nameservers
          </StyledTitlePage>
        </Grid>
      </StyledGridMasthead>

      <div>

        <Grid
          container
          direction="column"
          justifyContent="space-between"
          spacing={10}
        >
          <Grid container item xs={12} md={8}>
            <StyledForm noValidate autoComplete="off">

              <StyledFormControl component="fieldset" error={!!useZenbusinessNameserverError}>
                <StyledLegend component="legend">
                  <Text size="md">
                    Nameserver Options
                  </Text>
                </StyledLegend>
                <StyledDivider />
                <RadioGroup aria-label="use which nameservers" name="useZenbusinessNameservers" value={useZenbusinessNameserversValue.toString()} onChange={handleUseZenbusinessNameservers}>
                  <FormControlLabel value="true" control={<Radio color="primary" />} label={<Text size="sm">Use ZenBusiness nameservers</Text>} />
                  <FormControlLabel value="false" control={<Radio color="primary" />} label={<Text size="sm">Use custom nameservers</Text>} />
                </RadioGroup>
                {useZenbusinessNameserverError && (
                  <FormHelperText>
                    {
                      messages.validation.useZenbusinessNameservers[useZenbusinessNameserverError]
                        || messages.validation.generic
                    }
                  </FormHelperText>
                )}
              </StyledFormControl>

              {!useZenbusinessNameserversValue && (
                <>
                  <StyledFormControl component="fieldset">
                    <Grid container spacing={2}>
                      {records.map(({
                        key, value, required, error, disabled, label
                      }) => (
                      /* eslint-disable react/jsx-no-duplicate-props */
                        <Grid item xs={12} md={6} key={key}>
                          <StyledTextField
                            id={key}
                            variant="outlined"
                            size="medium"
                            label={messages.config[label]}
                            name={key}
                            value={value}
                            onChange={handleRecordChange}
                            helperText={error}
                            error={!!error}
                            required={required}
                            disabled={disabled}
                            inputProps={{
                              maxLength: 254
                            }}
                            InputProps={{
                              endAdornment: (showDelete && (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="remove nameserver record"
                                  onClick={handleDeleteNameserver(key)}
                                  edge="end"
                                  size="large"
                                >
                                  <StyledTrash src="/icon/micro/trash-small.svg" width={24} alt="" />
                                </IconButton>
                              </InputAdornment>
                              ))
                            }}
                          />
                        </Grid>
                      /* eslint-enable react/jsx-no-duplicate-props */
                      ))}
                    </Grid>
                  </StyledFormControl>
                  <StyledButton
                    variant="text"
                    color="primary"
                    onClick={handleAddNameserver}
                    disabled={loading}
                    sx={{ width: 'auto' }}
                  >
                    {`add ${records.length > 1 ? 'another' : 'a'} nameserver`}
                  </StyledButton>
                </>
              )}

              <StyledActions
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={2}
              >
                <Grid item xs={12} md={6}>
                  <StyledButton variant="outlined" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>cancel</StyledButton>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledButton variant="contained" color="primary" onClick={handleNameserverSave} disabled={loading || !unsaved}>save</StyledButton>
                </Grid>
              </StyledActions>
            </StyledForm>
          </Grid>

          <Grid item xs={12} md={8}>
            <StyledHelpTitle
              variant="sm"
              level={4}
            >
              Nameserver configuration
            </StyledHelpTitle>
            <Text size="md" gutterBottom>
              {messages.help.common}
            </Text>
            <Text size="md">
              {messages.help.nameserver}
            </Text>
          </Grid>

        </Grid>
      </div>
    </StyledDivRoot>
  );
};

export default DomainNameserverMutate;
